import axios from 'axios';
import API_BASE from '../apiBase';

// Tracking API base URL - use shared config
const TRACKING_API_BASE = API_BASE;

const trackingApi = axios.create({
    baseURL: TRACKING_API_BASE,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add JWT authentication interceptor
trackingApi.interceptors.request.use(
    (config) => {
        const accessToken = localStorage.getItem('floos_access_token');
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Helper function to convert data to FormData if image is present
const toFormData = (data: any): FormData | any => {
    if (data.image instanceof File) {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
            if (data[key] !== undefined && data[key] !== null) {
                if (key === 'image' && data[key] instanceof File) {
                    formData.append(key, data[key]);
                } else if (key !== 'image_url') {
                    formData.append(key, data[key]);
                }
            }
        });
        return formData;
    }
    // Remove image_url from data before sending
    const { image_url, image, ...rest } = data;
    return rest;
};

// Types
export interface Idea {
    id?: number;
    title: string;
    summary: string;
    category: 'algorithm' | 'ui' | 'performance' | 'data_quality' | 'ux' | 'infrastructure' | 'integration' | 'other';
    area?: string;
    problem?: string;
    proposed_solution?: string;
    expected_benefit?: string;
    implementation_notes?: string;
    priority: 'critical' | 'high' | 'medium' | 'low' | 'someday';
    effort?: 'trivial' | 'small' | 'medium' | 'large' | 'epic';
    impact?: 'minor' | 'moderate' | 'significant' | 'major' | 'transformative';
    status?: 'proposed' | 'approved' | 'in_progress' | 'implemented' | 'rejected' | 'deferred';
    tags?: string;
    proposed_by?: string;
    created_at?: string;
    updated_at?: string;
    implemented_at?: string;
    image?: File | null;
    image_url?: string;
}

export interface Bug {
    id?: number;
    title: string;
    description: string;
    status?: 'open' | 'in_progress' | 'resolved' | 'closed' | 'wont_fix';
    severity: 'minor' | 'moderate' | 'major' | 'critical';
    tool?: number | null;  // ForeignKey to Tool
    tool_name?: string;    // Read-only
    root_cause?: string;
    fix?: string;          // Was incorrectly named fix_applied
    is_resolved?: boolean; // Read-only
    reported_at?: string;  // Read-only (was created_at)
    resolved_at?: string;
    created_at?: string;   // Alias for reported_at
    image?: File | null;
    image_url?: string;
}

export interface Feature {
    id?: number;
    name: string;
    description: string;
    expected_behavior: string;
    category?: 'algorithms' | 'admin' | 'data-import' | 'ui' | 'api' | 'infrastructure' | 'other';
    status?: 'active' | 'deprecated' | 'planned';
    api_endpoints?: string;
    frontend_pages?: string;
    key_files?: string;
    dependencies?: string;
    created_at?: string;
    updated_at?: string;
    image?: File | null;
    image_url?: string;
}

export interface Research {
    id?: number;
    title: string;
    summary: string;
    findings: string;
    category?: 'algorithm_tuning' | 'data_analysis' | 'experiment' | 'discovery' | 'hypothesis' | 'calibration';
    area?: string;
    data_evidence?: string;
    methodology?: string;
    conclusion?: string;
    action_taken?: string;
    status?: 'validated' | 'invalidated' | 'pending' | 'superseded' | 'archived';
    affects_production?: boolean;
    tags?: string;
    researcher?: string;
    research_date?: string;
    created_at?: string;
    updated_at?: string;
    image?: File | null;
    image_url?: string;
}

// Ideas API
export const ideasApi = {
    getAll: async (params?: {
        category?: string;
        priority?: string;
        status?: string;
        search?: string;
    }) => {
        const response = await trackingApi.get('/api/v1/admin/ideas/', { params });
        return response.data;
    },

    get: async (id: number) => {
        const response = await trackingApi.get(`/api/v1/admin/ideas/${id}/`);
        return response.data;
    },

    create: async (idea: Idea) => {
        const data = toFormData(idea);
        const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
        const response = await trackingApi.post('/api/v1/admin/ideas/', data, config);
        return response.data;
    },

    update: async (id: number, idea: Partial<Idea>) => {
        const data = toFormData(idea);
        const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
        const response = await trackingApi.patch(`/api/v1/admin/ideas/${id}/`, data, config);
        return response.data;
    },

    delete: async (id: number) => {
        const response = await trackingApi.delete(`/api/v1/admin/ideas/${id}/`);
        return response.data;
    },

    getStats: async () => {
        const response = await trackingApi.get('/api/v1/admin/ideas/stats/summary/');
        return response.data;
    }
};

// Bugs API
export const bugsApi = {
    getAll: async (params?: {
        status?: string;
        severity?: string;
        category?: string;
        search?: string;
    }) => {
        const response = await trackingApi.get('/api/v1/admin/bugs/', { params });
        return response.data;
    },

    get: async (id: number) => {
        const response = await trackingApi.get(`/api/v1/admin/bugs/${id}/`);
        return response.data;
    },

    create: async (bug: Bug) => {
        const data = toFormData(bug);
        const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
        const response = await trackingApi.post('/api/v1/admin/bugs/', data, config);
        return response.data;
    },

    update: async (id: number, bug: Partial<Bug>) => {
        const data = toFormData(bug);
        const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
        const response = await trackingApi.patch(`/api/v1/admin/bugs/${id}/`, data, config);
        return response.data;
    },

    delete: async (id: number) => {
        const response = await trackingApi.delete(`/api/v1/admin/bugs/${id}/`);
        return response.data;
    },

    getStats: async () => {
        const response = await trackingApi.get('/api/v1/admin/bugs/stats/summary/');
        return response.data;
    }
};

// Features API
export const featuresApi = {
    getAll: async (params?: {
        category?: string;
        status?: string;
        search?: string;
    }) => {
        const response = await trackingApi.get('/api/v1/admin/features/', { params });
        return response.data;
    },

    get: async (id: number) => {
        const response = await trackingApi.get(`/api/v1/admin/features/${id}/`);
        return response.data;
    },

    create: async (feature: Feature) => {
        const data = toFormData(feature);
        const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
        const response = await trackingApi.post('/api/v1/admin/features/', data, config);
        return response.data;
    },

    update: async (id: number, feature: Partial<Feature>) => {
        const data = toFormData(feature);
        const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
        const response = await trackingApi.patch(`/api/v1/admin/features/${id}/`, data, config);
        return response.data;
    },

    delete: async (id: number) => {
        const response = await trackingApi.delete(`/api/v1/admin/features/${id}/`);
        return response.data;
    },

    getStats: async () => {
        const response = await trackingApi.get('/api/v1/admin/features/stats/summary/');
        return response.data;
    }
};

// Research API
export const researchApi = {
    getAll: async (params?: {
        category?: string;
        area?: string;
        status?: string;
        affects_production?: boolean;
        search?: string;
    }) => {
        const response = await trackingApi.get('/api/v1/admin/research/', { params });
        return response.data;
    },

    get: async (id: number) => {
        const response = await trackingApi.get(`/api/v1/admin/research/${id}/`);
        return response.data;
    },

    create: async (research: Research) => {
        const data = toFormData(research);
        const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
        const response = await trackingApi.post('/api/v1/admin/research/', data, config);
        return response.data;
    },

    update: async (id: number, research: Partial<Research>) => {
        const data = toFormData(research);
        const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
        const response = await trackingApi.patch(`/api/v1/admin/research/${id}/`, data, config);
        return response.data;
    },

    delete: async (id: number) => {
        const response = await trackingApi.delete(`/api/v1/admin/research/${id}/`);
        return response.data;
    },

    getStats: async () => {
        const response = await trackingApi.get('/api/v1/admin/research/stats/summary/');
        return response.data;
    }
};
